// libraries
import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

// components
import LoadingSpinner from "core/LoadingSpinner";
import NotFound from "core/NotFound";
import PageMeta from "core/PageMeta";
import CheckoutLayout from "core/CheckoutLayout";
import PrepaidCheckout from "icons/PrepaidCheckout";
import IconCheckout from "icons/IconCheckout";

// queries
import { GET_ICON_CHECKOUT_PAGE_DATA } from "./queries";

// helpers
import { getFormattedData } from "./helpers";
import {
    PRO_ANNUAL_DOWNLOAD,
    PRO_MONTHLY_DOWNLOAD,
    PRO_SINGLE_DOWNLOAD,
    PRO_PREPAID_DOWNLOAD
} from "icons/helpers";

// variables:
const stripePromise = loadStripe(window._nounConfig.stripePublishable);

const IconCheckoutPage = ({ match }) => {
    const { iconId, checkoutType } = match.params;

    const { loading, data } = useQuery(GET_ICON_CHECKOUT_PAGE_DATA, {
        fetchPolicy: "cache-and-network",
        skip: !iconId,
        variables: {
            iconId
        }
    });

    if (loading) {
        return <LoadingSpinner />;
    }

    const isPrepaid = checkoutType === PRO_PREPAID_DOWNLOAD;

    const {
        iconDetail: {
            slugWithId,
            title,
            creator,
            thumbnails: { thumbnail200 },
            url
        }
    } = data;
    const { pageTitle, formSectionHeading, hasData } = getFormattedData({
        isPrepaid,
        title,
        iconId,
        data
    });

    const isNotFound = (!hasData && !loading) || !data;

    const CheckoutLayoutComponent = ({ children }) => {
        return (
            <CheckoutLayout
                pageHeading="Purchase Icon"
                formSectionHeading={formSectionHeading}
                thumbnail={thumbnail200}
                imageTitle={title}
                slugWithId={slugWithId}
                creator={creator}
                isPrepaid={isPrepaid}
                type="icons"
            >
                {children}
            </CheckoutLayout>
        );
    };

    // if checkoutType matches one of our two types, render appropriate component for that route. otherwise, render NotFound:
    const CheckoutTypeComponent = () => {
        if (
            isPrepaid ||
            checkoutType === PRO_SINGLE_DOWNLOAD ||
            checkoutType === PRO_ANNUAL_DOWNLOAD ||
            checkoutType === PRO_MONTHLY_DOWNLOAD
        ) {
            return isPrepaid ? (
                <CheckoutLayoutComponent>
                    <PrepaidCheckout
                        thumbnail={thumbnail200}
                        title={title}
                        slugWithId={slugWithId}
                        iconId={iconId}
                    />
                </CheckoutLayoutComponent>
            ) : (
                <CheckoutLayoutComponent>
                    <Elements stripe={stripePromise}>
                        <IconCheckout
                            title={title}
                            thumbnail={thumbnail200}
                            slugWithId={slugWithId}
                            iconId={iconId}
                            iconDetailPageUrl={url}
                        />
                    </Elements>
                </CheckoutLayoutComponent>
            );
        }
        return <NotFound />;
    };

    return isNotFound ? (
        <NotFound />
    ) : (
        <>
            {hasData && <PageMeta title={pageTitle} noindex />}

            <CheckoutTypeComponent />
        </>
    );
};

export default IconCheckoutPage;
